import axios from "axios";
import TokenService from "@/services/token.service";
import { AppError } from "@/services/error.service";

axios.defaults.baseURL = process.env.VUE_APP_BASE_API;
// axios.defaults.timeout = 5000; // 5s Timeout

const RequestService = {
  // ----------------------------------------- GET -----------------------------------------
  get(path, params, headers) {
    return this.request("GET", path, null, params, headers);
  },

  // ----------------------------------------- GET BLOB -----------------------------------------
  getBlob(path, params, headers, fullResponse) {
    return this.request("GET", path, null, params, headers, "blob", fullResponse);
  },

  // ----------------------------------------- POST -----------------------------------------
  post(path, data, params, headers) {
    return this.request("POST", path, data, params, headers);
  },

  // ----------------------------------------- POST FILE -----------------------------------------
  postFile(path, file, name, customPostData, headers) {
    if (!headers) {
      headers = TokenService.getHeader();
      headers["Content-Type"] = "multipart/form-data";
    }

    const formData = customPostData || new FormData();
    if (!customPostData) {
      if (file) formData.append("file", file);
      if (name) formData.append("name", name);
    }
    return this.request("POST", path, formData, null, headers);
  },

  // ----------------------------------------- POST FORM DATA -----------------------------------------
  postFormData(path, formData, headers) {
    if (!headers) {
      headers = TokenService.getHeader();
      headers["Content-Type"] = "multipart/form-data";
    }

    return this.request("POST", path, formData, null, headers);
  },

  // ----------------------------------------- PUT -----------------------------------------
  put(path, data, params, headers) {
    return this.request("PUT", path, data, params, headers);
  },

  // ----------------------------------------- PUT FORM DATA -----------------------------------------
  putFormData(path, formData, headers) {
    return this.request("PUT", path, formData, null, headers);
  },

  // ----------------------------------------- DELETE -----------------------------------------
  delete(path, params, headers) {
    return this.request("DELETE", path, null, params, headers);
  },

  // ----------------------------------------- CUSTOM REQUEST -----------------------------------------
  customRequest(data) {
    return this.request(data.method, data.url, data.data, data.params, data.headers, data.responseType);
  },

  // ----------------------------------------- HANDLE LIST -----------------------------------------
  _handleList(response) {
    let res = response.data.results;
    res._metadata = {
      count: response.data.count,
      next: response.data.next,
      previous: response.data.previous,
    };
    return res;
  },

  // ----------------------------------------- GET MORE -----------------------------------------
  async getMore(url) {
    try {
      return await RequestService.get(url);
    } catch (error) {
      throw new AppError(error);
    }
  },

  // ----------------------------------------- GET PAGE -----------------------------------------
  async getPage(path, page, filters) {
    try {
      const newFilters = {
        ...filters,
        limit: 50,
        offset: (page - 1) * 50,
      };
      return await RequestService.get(path, newFilters);
    } catch (error) {
      throw new AppError(error);
    }
  },

  // ----------------------------------------- REQUEST -----------------------------------------
  refreshPromise: null,
  async request(method, url, data, params, headers, responseType, fullResponse = false) {
    const doRefresh = async function () {
      console.log("Refreshing token...");
      // Refresh token
      try {
        RequestService.refreshPromise = TokenService.refreshAccessToken();
        await RequestService.refreshPromise;
        if (headers) {
          headers["Authorization"] = `Bearer ${TokenService.accessToken}`;
        } else {
          headers = TokenService.getHeader();
        }
        return headers;
      } catch (error) {
        console.error("Token refresh failed:", error);
        throw error;
      } finally {
        RequestService.refreshPromise = null;
      }
    };

    // Make request
    const makeRequest = async function () {
      const request = {
        method: method,
        url: url,
        data: data,
        params: params,
        headers: headers || TokenService.getHeader(),
        responseType: responseType,
      };

      try {
        return axios(request).then((response) => {
          if (fullResponse) {
            return response;
          } else if (response.data.results != undefined) {
            return RequestService._handleList(response);
          } else {
            return response.data;
          }
        });
      } catch (error) {
        // Check for 401 error
        if (error && error.status == 401 && !url.includes("/token")) {
          console.error("Error 401:", error);
          await doRefresh();
          // Make original request
          return makeRequest();
        } else {
          throw error;
        }
      }
    };

    // Wait for ongoing token refresh
    if (RequestService.refreshPromise) {
      await RequestService.refreshPromise;
    }

    // Check if token expired
    // TokenService.getRefreshToken();
    if (TokenService.accessTokenExpired) {
      await doRefresh();
      // Make original request
      return makeRequest();
    } else {
      // Make request
      return makeRequest();
    }
  },

  /**
   * Format date for API
   * @param {Date} date
   * @returns
   */
  formatDate(date) {
    // YYYY-MM-DDThh:mm[:ss[.uuuuuu]][+HH:MM|-HH:MM|Z]
    if (typeof date == "object") {
      return date.toISOString();
    } else {
      return date;
    }
  },

  /**
   * Replace array of object with array of ids
   * @param {Array} data [{ id: 1, name: "Test 1"},  id: 2, name: "Test 2"}]
   * @param {String} key default: id
   * @returns {String[]} [1,2]
   */
  replaceWithIds(data, key = "id") {
    return data.map((element) => element[key]);
  },
};

export default RequestService;
